import { useState, useEffect, useCallback } from 'react';

export interface IElapsedTimeDisplay {
  elapsedSeconds: number;
  start: () => void;
  stop: () => void;
  reset: () => void;
}

/**
 * Hook that tracks and displays elapsed time with start/stop/reset controls.
 * Time accumulates across pause/resume cycles.
 * @returns Object with formatted time string and control functions
 */
export function useElapsedTimeDisplay(): IElapsedTimeDisplay {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning]);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  const reset = useCallback(() => {
    setIsRunning(false);
    setElapsedSeconds(0);
  }, []);

  return {
    elapsedSeconds,
    start,
    stop,
    reset,
  };
}

export default useElapsedTimeDisplay;
